import { queueEvent, addBeforeHide } from "./buffer";
import {
  blockIsOnScreen,
  layoutFor,
  noteInput,
  noteScroll,
  openVisit,
  setMode,
  setVisibleBlocks,
  snapshot,
  type Layout,
  type Via,
  type Visit,
} from "./visit";

export type { Via };

type OpenArgs = {
  docRoot: HTMLElement;
  docCount: number;
  documentId: string;
  via: Via;
  fromDocumentId: string | null;
  phaseKey: string;
  phaseElapsedMs: number;
};

let current: ReadingVisit | null = null;

export function openReadingVisit(args: OpenArgs): void {
  try {
    if (current && !current.closed) {
      current.checkpoint();
      current.detach();
    }
    current = new ReadingVisit(args);
    current.start();
  } catch (error) {
    console.warn("openReadingVisit failed", error);
  }
}

export function closeReadingVisit(final: boolean): void {
  try {
    current?.close(final);
  } catch (error) {
    console.warn("closeReadingVisit failed", error);
  }
}

export function detachReadingVisit(): void {
  try {
    if (current?.closed) current.detach();
  } catch {
    /* already gone */
  }
}

class ReadingVisit {
  closed = false;
  visit: Visit;
  private readonly docRoot: HTMLElement;
  private readonly docCount: number;
  private readonly phaseKey: string;
  private readonly cleanups: Array<() => void> = [];
  private candidates = new Set<string>();
  private observer: IntersectionObserver | null = null;
  private idleTimer = 0;
  private checkpointTimer = 0;
  private rectTimer = 0;
  private lastMeasure = 0;
  private removeHide: (() => void) | null = null;

  constructor(args: OpenArgs) {
    this.docRoot = args.docRoot;
    this.docCount = args.docCount;
    this.phaseKey = args.phaseKey;
    const layout = this.currentLayout();
    this.visit = openVisit({
      visitId: crypto.randomUUID(),
      documentId: args.documentId,
      via: args.via,
      fromDocumentId: args.fromDocumentId,
      phaseElapsedMs: args.phaseElapsedMs,
      openedAt: Date.now(),
      layout,
      viewport: this.viewportSize(layout),
      mode: this.modeNow(),
    });
  }

  start(): void {
    try {
      void queueEvent(
        "document_open",
        {
          document_id: this.visit.documentId,
          via: this.visit.via,
          from_document_id: this.visit.fromDocumentId,
        },
        this.phaseKey,
      );
      this.bindObserver();
      this.measure(true);
      this.sampleDepth();
      if (this.visit.mode === "active") this.armIdle();
      this.removeHide = addBeforeHide(() => this.onHidden());
      this.checkpointTimer = window.setInterval(() => {
        try {
          if (!this.closed) this.checkpoint();
        } catch {
          /* tracking */
        }
      }, 60_000);
      this.listen("scroll", (event) => this.onScroll(event), true);
      this.listen("pointerdown", () => this.onActivity(), true);
      this.listen("keydown", (event) => this.onKey(event), true);
      this.listen("wheel", () => this.onActivity(), true);
      this.listen("touchstart", () => this.onActivity(), true);
      this.listen("visibilitychange", () => this.onVisibility());
      this.listen("copy", () => this.onCopy());
      this.onWindow("blur", () => this.onBlur());
      this.onWindow("focus", () => this.onFocus());
      this.onWindow("resize", () => this.onResize());
    } catch (error) {
      console.warn("reading tracker failed to start", error);
    }
  }

  close(final: boolean): void {
    if (this.closed) return;
    this.closed = true;
    try {
      this.emit(final);
    } catch {
      /* tracking */
    }
    this.detach();
    if (current === this) current = null;
  }

  checkpoint(): void {
    if (this.closed) return;
    try {
      this.emit(false);
    } catch {
      /* tracking */
    }
  }

  detach(): void {
    this.closed = true;
    this.observer?.disconnect();
    this.observer = null;
    window.clearInterval(this.checkpointTimer);
    window.clearTimeout(this.idleTimer);
    window.clearTimeout(this.rectTimer);
    this.removeHide?.();
    this.removeHide = null;
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    if (current === this) current = null;
  }

  private emit(final: boolean): void {
    const dwell = snapshot(this.visit, Date.now(), final);
    void queueEvent("document_dwell", dwell as unknown as Record<string, unknown>, this.phaseKey);
  }

  private onHidden(): void {
    if (this.closed) return;
    const now = Date.now();
    setVisibleBlocks(this.visit, [], now);
    setMode(this.visit, "hidden", now);
    this.checkpoint();
  }

  private onVisibility(): void {
    if (this.closed) return;
    if (document.visibilityState === "hidden") {
      setMode(this.visit, "hidden", Date.now());
      return;
    }
    const focused = document.hasFocus();
    setMode(this.visit, focused ? "active" : "unfocused", Date.now());
    if (focused) this.armIdle();
    this.measure(true);
  }

  private onBlur(): void {
    if (this.closed || document.visibilityState === "hidden") return;
    setMode(this.visit, "unfocused", Date.now());
  }

  private onFocus(): void {
    if (this.closed || document.visibilityState === "hidden") return;
    setMode(this.visit, "active", Date.now());
    this.armIdle();
  }

  private onActivity(): void {
    if (this.closed) return;
    const now = Date.now();
    noteInput(this.visit, now);
    if (document.visibilityState === "visible" && document.hasFocus()) {
      setMode(this.visit, "active", now);
      this.armIdle();
    }
  }

  private onKey(event: Event): void {
    this.onActivity();
    if (this.closed) return;
    const key = event as KeyboardEvent;
    if ((key.ctrlKey || key.metaKey) && key.key.toLowerCase() === "f") {
      void queueEvent("find", { key: "f" }, this.phaseKey);
    }
  }

  private onCopy(): void {
    if (this.closed) return;
    const selection = document.getSelection();
    const text = selection?.toString() ?? "";
    if (!text) return;
    const node = selection?.anchorNode ?? null;
    const el = node instanceof Element ? node : node?.parentElement ?? null;
    const blockId = el?.closest("[data-bid]")?.getAttribute("data-bid") ?? null;
    void queueEvent("copy", { block_id: blockId, length: text.length }, this.phaseKey);
  }

  private onScroll(event: Event): void {
    if (this.closed) return;
    const scroller = this.scrollerElement();
    if (!acceptsScrollTarget(event.target, scroller)) return;
    this.onActivity();
    this.sampleDepth();
    this.scheduleMeasure();
  }

  private onResize(): void {
    if (this.closed) return;
    this.visit.layout = this.currentLayout();
    this.visit.viewport = this.viewportSize(this.visit.layout);
    this.bindObserver();
    this.scheduleMeasure();
  }

  private sampleDepth(): void {
    const scroller = this.scrollerElement();
    let depth = 0;
    if (scroller === "page") {
      const total = document.documentElement.scrollHeight;
      depth = total <= 0 ? 0 : (window.scrollY + window.innerHeight) / total;
    } else {
      const total = scroller.scrollHeight;
      depth = total <= 0 ? 0 : (scroller.scrollTop + scroller.clientHeight) / total;
    }
    noteScroll(this.visit, depth, Date.now());
  }

  private scheduleMeasure(): void {
    const now = Date.now();
    if (now - this.lastMeasure >= 200) {
      this.measure(false);
      return;
    }
    if (this.rectTimer) return;
    this.rectTimer = window.setTimeout(() => {
      this.rectTimer = 0;
      this.measure(false);
    }, 200);
  }

  private measure(force: boolean): void {
    if (this.closed) return;
    const now = Date.now();
    if (!force && now - this.lastMeasure < 200) return;
    this.lastMeasure = now;
    if (document.visibilityState !== "visible") {
      setVisibleBlocks(this.visit, [], now);
      return;
    }
    const scroller = this.scrollerElement();
    const view = scroller === "page" ? null : scroller.getBoundingClientRect();
    const viewportHeight = view ? view.height : window.innerHeight;
    const visible: string[] = [];
    for (const id of this.candidates) {
      const el = this.docRoot.querySelector(`[data-bid="${id}"]`);
      if (!(el instanceof HTMLElement)) continue;
      const rect = el.getBoundingClientRect();
      const top = view ? rect.top - view.top : rect.top;
      const bottom = view ? rect.bottom - view.top : rect.bottom;
      if (blockIsOnScreen({ top, bottom }, viewportHeight)) visible.push(id);
    }
    setVisibleBlocks(this.visit, visible, now);
    this.visit.layout = scroller === "page" ? "page" : "panel";
    this.visit.viewport = this.viewportSize(this.visit.layout);
  }

  private bindObserver(): void {
    this.observer?.disconnect();
    const scroller = this.scrollerElement();
    this.candidates = new Set();
    for (const el of this.docRoot.querySelectorAll("[data-bid]")) {
      const id = el.getAttribute("data-bid");
      if (id) this.candidates.add(id);
    }
    if (typeof IntersectionObserver === "undefined") return;
    this.observer = new IntersectionObserver(
      (entries) => {
        try {
          if (this.closed) return;
          for (const entry of entries) {
            const id = (entry.target as HTMLElement).getAttribute("data-bid");
            if (!id) continue;
            if (entry.isIntersecting) this.candidates.add(id);
            else this.candidates.delete(id);
          }
          this.measure(true);
        } catch {
          /* tracking */
        }
      },
      { root: scroller === "page" ? null : scroller, threshold: 0 },
    );
    for (const el of this.docRoot.querySelectorAll("[data-bid]")) this.observer.observe(el);
  }

  private currentLayout(): Layout {
    return layoutFor(this.docCount, window.innerWidth);
  }

  private scrollerElement(): HTMLElement | "page" {
    if (this.currentLayout() !== "panel") return "page";
    const panel = this.docRoot.closest(".reading-main");
    return panel instanceof HTMLElement ? panel : "page";
  }

  private viewportSize(layout: Layout): [number, number] {
    if (layout === "panel") {
      const panel = this.docRoot.closest(".reading-main");
      if (panel instanceof HTMLElement) return [panel.clientWidth, panel.clientHeight];
    }
    return [window.innerWidth, window.innerHeight];
  }

  private modeNow(): "hidden" | "unfocused" | "active" {
    if (document.visibilityState === "hidden") return "hidden";
    if (!document.hasFocus()) return "unfocused";
    return "active";
  }

  private armIdle(): void {
    window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => {
      try {
        if (this.closed) return;
        if (document.visibilityState !== "visible" || !document.hasFocus()) return;
        if (Date.now() - this.visit.lastInputAt < 60_000) return;
        setMode(this.visit, "idle", Date.now());
      } catch {
        /* tracking */
      }
    }, 60_000);
  }

  private listen(type: string, fn: (event: Event) => void, capture = false): void {
    const wrapped = (event: Event) => {
      try {
        fn(event);
      } catch {
        /* tracking */
      }
    };
    document.addEventListener(type, wrapped, capture);
    this.cleanups.push(() => document.removeEventListener(type, wrapped, capture));
  }

  private onWindow(type: string, fn: () => void): void {
    const wrapped = () => {
      try {
        fn();
      } catch {
        /* tracking */
      }
    };
    window.addEventListener(type, wrapped);
    this.cleanups.push(() => window.removeEventListener(type, wrapped));
  }
}

export function acceptsScrollTarget(target: EventTarget | null, scroller: HTMLElement | "page"): boolean {
  if (target instanceof Element && target.closest(".doc-nav-list")) return false;
  if (scroller === "page") {
    return target === document || target === document.documentElement || target === document.body;
  }
  return target === scroller;
}
